// src/components/VendorDocumentsTab.js
// UN EOI Phase 5: vendor document registry — contracts, certifications, insurance, SOC
// reports etc. with a review date. The backend reminder worker nags the vendor owner starting
// ReminderDays before the review date. Managers upload/edit/delete; everyone with vendor
// read access can view and download.
import React, { useState, useEffect, useCallback } from "react";
import { FileText, Upload, Trash2, Download, Pencil, X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import Button from "./Buttons";

const DOCUMENT_TYPES = ["Contract", "Certification", "Insurance", "SOC Report", "Policy", "Other"];

const formatBytes = (bytes) => {
    if (bytes == null) return "—";
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
};

const reviewBadge = (reviewDate) => {
    if (!reviewDate) return null;
    const days = Math.floor((new Date(reviewDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { text: `Review overdue ${-days}d`, color: "bg-red-100 text-red-800" };
    if (days <= 30) return { text: `Review in ${days}d`, color: "bg-amber-100 text-amber-800" };
    return { text: `Review ${new Date(reviewDate).toLocaleDateString()}`, color: "bg-gray-100 text-gray-600" };
};

const VendorDocumentsTab = ({ vendorId, canManage }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadForm, setUploadForm] = useState({ file: null, documentType: "", description: "", reviewDate: "", reminderDays: 30 });
    const [editingDoc, setEditingDoc] = useState(null);
    const [editForm, setEditForm] = useState({ documentType: "", description: "", reviewDate: "", reminderDays: 30 });
    const [busy, setBusy] = useState(false);
    const apiCall = useApi();

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiCall("GET", `/TPRMVendor/${vendorId}/documents`);
            setDocuments(Array.isArray(data) ? data : []);
        } catch (err) {
            setDocuments([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    useEffect(() => {
        if (vendorId) fetchDocuments();
    }, [vendorId, fetchDocuments]);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!uploadForm.file) return;

        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append("file", uploadForm.file);
            if (uploadForm.documentType) formData.append("documentType", uploadForm.documentType);
            if (uploadForm.description) formData.append("description", uploadForm.description);
            if (uploadForm.reviewDate) formData.append("reviewDate", uploadForm.reviewDate);
            formData.append("reminderDays", String(uploadForm.reminderDays || 30));

            await apiCall("POST", `/TPRMVendor/${vendorId}/documents`, formData, true);
            setShowUpload(false);
            setUploadForm({ file: null, documentType: "", description: "", reviewDate: "", reminderDays: 30 });
            await fetchDocuments();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to upload document.");
        } finally {
            setUploading(false);
        }
    };

    const startEdit = (doc) => {
        setEditingDoc(doc);
        setEditForm({
            documentType: doc.documentType || "",
            description: doc.description || "",
            reviewDate: doc.reviewDate ? doc.reviewDate.substring(0, 10) : "",
            reminderDays: doc.reminderDays || 30,
        });
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await apiCall("PUT", `/TPRMVendor/${vendorId}/documents/${editingDoc.id}`, {
                documentType: editForm.documentType || null,
                description: editForm.description || null,
                reviewDate: editForm.reviewDate || null,
                reminderDays: editForm.reminderDays || null,
            });
            setEditingDoc(null);
            await fetchDocuments();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to update document.");
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.documentName}"?`)) return;
        setBusy(true);
        setError(null);
        try {
            await apiCall("DELETE", `/TPRMVendor/${vendorId}/documents/${doc.id}`);
            await fetchDocuments();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete document.");
        } finally {
            setBusy(false);
        }
    };

    const handleDownload = async (doc) => {
        try {
            const blob = await apiCall("GET", `/TPRMVendor/${vendorId}/documents/${doc.id}/download`, null, false, "blob");
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", doc.documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError("Failed to download document.");
        }
    };

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-content-primary">Vendor Documents</h3>
                    <p className="text-sm text-content-secondary mt-1">
                        Contracts, certifications and reports for this vendor. Documents with a review date
                        trigger automatic reminders to the vendor owner ahead of the review.
                    </p>
                </div>
                {canManage && (
                    <Button size="sm" onClick={() => setShowUpload(true)}>
                        <Upload className="h-4 w-4 mr-1" />
                        Upload Document
                    </Button>
                )}
            </div>

            {error && (
                <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading ? (
                <p className="text-sm text-gray-500 py-4">Loading documents...</p>
            ) : documents.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No documents uploaded for this vendor yet.</p>
            ) : (
                <div className="divide-y divide-gray-100">
                    {documents.map((doc) => {
                        const badge = reviewBadge(doc.reviewDate);
                        return (
                            <div key={doc.id} className="flex items-center justify-between py-3 gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{doc.documentName}</p>
                                        <p className="text-xs text-gray-500">
                                            {doc.documentType || "Document"} · {formatBytes(doc.fileSize)}
                                            {doc.uploadedByUserName ? ` · ${doc.uploadedByUserName}` : ""}
                                            {doc.description ? ` — ${doc.description}` : ""}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {badge && (
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>
                                            {badge.text}
                                        </span>
                                    )}
                                    <button type="button" title="Download" disabled={busy}
                                        className="text-gray-400 hover:text-blue-600 disabled:opacity-50"
                                        onClick={() => handleDownload(doc)}>
                                        <Download className="h-4 w-4" />
                                    </button>
                                    {canManage && (
                                        <>
                                            <button type="button" title="Edit metadata" disabled={busy}
                                                className="text-gray-400 hover:text-blue-600 disabled:opacity-50"
                                                onClick={() => startEdit(doc)}>
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button type="button" title="Delete" disabled={busy}
                                                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                onClick={() => handleDelete(doc)}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload modal */}
            {showUpload && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border max-w-lg w-full shadow-lg rounded-md bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Upload Vendor Document</h3>
                            <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleUpload} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, DOCX, TXT, CSV, XLSX — max 10 MB)</label>
                                <input type="file" accept=".pdf,.docx,.txt,.csv,.xlsx" required
                                    className="block w-full text-sm text-gray-700"
                                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                                <select value={uploadForm.documentType}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value })}>
                                    <option value="">-- Select --</option>
                                    {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <input type="text" value={uploadForm.description} maxLength={2000}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Date</label>
                                    <input type="date" value={uploadForm.reviewDate}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        onChange={(e) => setUploadForm({ ...uploadForm, reviewDate: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remind (days before)</label>
                                    <input type="number" min={1} max={365} value={uploadForm.reminderDays}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        onChange={(e) => setUploadForm({ ...uploadForm, reminderDays: parseInt(e.target.value) || 30 })} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowUpload(false)}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                    Cancel
                                </button>
                                <button type="submit" disabled={uploading || !uploadForm.file}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                                    {uploading ? "Uploading..." : "Upload"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit metadata modal */}
            {editingDoc && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border max-w-lg w-full shadow-lg rounded-md bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Document</h3>
                            <button onClick={() => setEditingDoc(null)} className="text-gray-400 hover:text-gray-600">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mb-3 truncate">{editingDoc.documentName}</p>
                        <form onSubmit={handleEditSave} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                                <select value={editForm.documentType}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    onChange={(e) => setEditForm({ ...editForm, documentType: e.target.value })}>
                                    <option value="">-- Select --</option>
                                    {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <input type="text" value={editForm.description} maxLength={2000}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Date</label>
                                    <input type="date" value={editForm.reviewDate}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        onChange={(e) => setEditForm({ ...editForm, reviewDate: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remind (days before)</label>
                                    <input type="number" min={1} max={365} value={editForm.reminderDays}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        onChange={(e) => setEditForm({ ...editForm, reminderDays: parseInt(e.target.value) || 30 })} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setEditingDoc(null)}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                    Cancel
                                </button>
                                <button type="submit" disabled={busy}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                                    {busy ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default VendorDocumentsTab;
